import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { MediaPicker } from '@/components/MediaPicker';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Copy,
  Eye,
  FileJson,
  Pencil,
  Plus,
  RefreshCw,
  Send,
  Trash2,
} from 'lucide-react';
import { PostedList } from '@/components/PostedList';
import { postedKey, refreshPostedSoon, usePosted } from '@/lib/posted';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ChannelPicker, CHANNEL_TYPE } from '@/components/ChannelPicker';
import { Switch } from '@/components/ui/switch';
import { renderDiscordText } from '@/lib/discordText';
import { useMentionNames, type MentionNames } from '@/lib/mentions';

/**
 * Discohook-style embed composer.
 *
 * Left column: editor (content textarea + collapsible embed cards, each with
 * every Discord embed field). Right column: live Discord-flavoured preview.
 * A JSON toggle exchanges the visual editor for a raw JSON payload for
 * copy/paste with Discohook.
 */

interface EmbedField {
  name: string;
  value: string;
  inline?: boolean;
}
interface Embed {
  title?: string;
  description?: string;
  url?: string;
  color?: number;
  author?: { name?: string; iconUrl?: string; url?: string };
  thumbnailUrl?: string;
  imageUrl?: string;
  footer?: { text?: string; iconUrl?: string };
  timestamp?: true | string;
  fields?: EmbedField[];
}
interface Payload {
  content: string;
  embeds: Embed[];
  /** "Leise senden": mentions show, nobody gets pinged. */
  silent?: boolean;
}

const EMPTY_EMBED: Embed = { color: 0x5865f2 };

const emptyPayload = (): Payload => ({
  content: '',
  embeds: [{ ...EMPTY_EMBED, title: 'Neues Embed', description: 'Text hier…' }],
});

export function EmbedComposer() {
  const { guildId } = useParams<{ guildId: string }>();
  const [channelId, setChannelId] = useState<string | null>(null);
  const [payload, setPayload] = useState<Payload>(emptyPayload);
  const [jsonMode, setJsonMode] = useState(false);
  const [jsonDraft, setJsonDraft] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const qc = useQueryClient();

  // Edit mode (?edit=<postedId>, from "Gepostete Nachrichten"): load the
  // posted message, then "Aktualisieren" overwrites it in Discord.
  const [params, setParams] = useSearchParams();
  const editId = params.get('edit');
  const posted = usePosted(guildId!);
  const editing = editId ? posted.data?.posted.find((p) => p.id === editId) : undefined;
  useEffect(() => {
    if (!editing) return;
    const d = editing.data as { content?: string; embeds?: Embed[]; silent?: boolean };
    setPayload({ content: d.content ?? '', embeds: Array.isArray(d.embeds) ? d.embeds : [], silent: !!d.silent });
    setChannelId(editing.channelId);
    setJsonMode(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing?.id]);
  const stopEditing = () => {
    params.delete('edit');
    setParams(params, { replace: true });
    setPayload(emptyPayload());
  };

  const sendMut = useMutation({
    mutationFn: (body: object) =>
      api('/api/actions/send-embed', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => refreshPostedSoon(qc, guildId!),
  });

  const updateMut = useMutation({
    mutationFn: () =>
      api<{ updated?: boolean; queued?: boolean }>(`/api/guilds/${guildId}/posted/${editId}/update`, {
        method: 'POST',
        body: JSON.stringify({ data: cleanPayload(payload) }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: postedKey(guildId!) }),
  });

  const canSend =
    !!channelId &&
    (payload.content.trim().length > 0 || payload.embeds.length > 0) &&
    !sendMut.isPending;

  const updateEmbed = (i: number, patch: Partial<Embed>) => {
    setPayload((p) => ({
      ...p,
      embeds: p.embeds.map((e, j) => (j === i ? { ...e, ...patch } : e)),
    }));
  };

  const enterJsonMode = () => {
    setJsonDraft(JSON.stringify(cleanPayload(payload), null, 2));
    setJsonError(null);
    setJsonMode(true);
  };
  const exitJsonMode = () => {
    try {
      const parsed = JSON.parse(jsonDraft) as Payload;
      if (typeof parsed !== 'object' || parsed === null) throw new Error('root must be object');
      const embeds = Array.isArray(parsed.embeds) ? parsed.embeds : [];
      setPayload({ content: parsed.content ?? '', embeds, silent: !!parsed.silent });
      setJsonError(null);
      setJsonMode(false);
    } catch (err) {
      setJsonError((err as Error).message);
    }
  };

  const send = () => {
    const body = { guildId, channelId, ...cleanPayload(payload) };
    sendMut.mutate(body);
  };

  // Role / member names for the mentions in the preview.
  const names = useMentionNames(guildId, [
    payload.content,
    ...payload.embeds.flatMap((e) => [e.title, e.description, ...(e.fields ?? []).map((f) => f.value)]),
  ]);

  return (
    <div className="mx-auto max-w-6xl">
      <Link
        to={`/server/${guildId}`}
        className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Zurück zur Server-Übersicht
      </Link>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">Embed Composer</h1>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => (jsonMode ? exitJsonMode() : enterJsonMode())}
          >
            {jsonMode ? (
              <>
                <Eye className="mr-1 h-4 w-4" /> Visueller Editor
              </>
            ) : (
              <>
                <FileJson className="mr-1 h-4 w-4" /> JSON-Modus
              </>
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              navigator.clipboard.writeText(
                JSON.stringify(cleanPayload(payload), null, 2)
              )
            }
          >
            <Copy className="mr-1 h-4 w-4" /> JSON kopieren
          </Button>
        </div>
      </div>

      {editId && (
        <Card className="mb-4 border-primary/60">
          <CardContent className="flex flex-wrap items-center gap-3 pt-6">
            <Pencil className="h-4 w-4 text-primary" />
            <div className="min-w-0 flex-1 text-sm">
              {editing ? (
                <>
                  Du bearbeitest eine gepostete Nachricht. <b>Aktualisieren</b> überschreibt sie in Discord.
                </>
              ) : posted.isLoading ? (
                'Lade Nachricht…'
              ) : (
                'Diese gepostete Nachricht gibt es nicht mehr.'
              )}
              {updateMut.isSuccess && (
                <span className="ml-2 text-success">
                  {updateMut.data?.queued ? 'An den Bot geschickt' : 'Aktualisiert ✓'}
                </span>
              )}
              {updateMut.error && (
                <span className="ml-2 text-destructive">{(updateMut.error as Error).message}</span>
              )}
            </div>
            <Button variant="outline" onClick={stopEditing}>
              Bearbeiten beenden
            </Button>
            <Button disabled={!editing || updateMut.isPending || jsonMode} onClick={() => updateMut.mutate()}>
              <RefreshCw className={updateMut.isPending ? 'mr-1 h-4 w-4 animate-spin' : 'mr-1 h-4 w-4'} />
              Aktualisieren
            </Button>
          </CardContent>
        </Card>
      )}

      <Card className={editId ? 'mb-4 hidden' : 'mb-4'}>
        <CardContent className="flex flex-wrap items-end gap-3 pt-6">
          <div className="min-w-[240px] flex-1">
            <Label>Ziel-Channel</Label>
            <ChannelPicker
              guildId={guildId!}
              value={channelId}
              onChange={setChannelId}
              types={[CHANNEL_TYPE.GUILD_TEXT, CHANNEL_TYPE.GUILD_ANNOUNCEMENT]}
            />
          </div>
          <div className="flex items-center gap-2">
            {sendMut.isSuccess && (
              <span className="text-sm text-success">Gesendet ✓</span>
            )}
            {sendMut.error && (
              <span className="text-sm text-destructive">
                {(sendMut.error as Error).message}
              </span>
            )}
            <Button disabled={!canSend} onClick={send}>
              <Send className="mr-1 h-4 w-4" />
              {sendMut.isPending ? 'Sende…' : 'Senden'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div>
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Editor
          </h2>
          {jsonMode ? (
            <Card>
              <CardContent className="pt-6">
                <Textarea
                  value={jsonDraft}
                  onChange={(e) => setJsonDraft(e.target.value)}
                  className="min-h-[400px] font-mono text-xs"
                  spellCheck={false}
                />
                {jsonError && (
                  <p className="mt-2 text-sm text-destructive">JSON-Fehler: {jsonError}</p>
                )}
                <p className="mt-2 text-xs text-muted-foreground">
                  Klick „Visueller Editor" um das JSON zu parsen und
                  zurückzuwechseln. Kompatibel mit Discohook-Payloads.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              <Card>
                <CardContent className="pt-6">
                  <Label>Text (optional)</Label>
                  <Textarea emoji
                    value={payload.content}
                    onChange={(e) =>
                      setPayload((p) => ({ ...p, content: e.target.value }))
                    }
                    placeholder="Nachricht über den Embeds (bis 2000 Zeichen)"
                    className="min-h-[80px]"
                    maxLength={2000}
                  />
                  <div className="mt-1 flex items-center justify-between gap-3 text-xs text-muted-foreground">
                    <Switch
                      checked={!!payload.silent}
                      onChange={(e) => setPayload((p) => ({ ...p, silent: e.target.checked }))}
                      label="Leise senden (niemand wird gepingt)"
                      className="text-xs"
                    />
                    <span>{payload.content.length} / 2000</span>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    <b>@</b> tippen für Rollen und Personen. Gepingt wird nur, wer hier im Text steht —
                    Erwähnungen in Embeds werden nur angezeigt.
                  </p>
                </CardContent>
              </Card>

              {payload.embeds.map((embed, i) => (
                <EmbedEditor
                  key={i}
                  index={i}
                  guildId={guildId!}
                  embed={embed}
                  onChange={(patch) => updateEmbed(i, patch)}
                  onRemove={() =>
                    setPayload((p) => ({
                      ...p,
                      embeds: p.embeds.filter((_, j) => j !== i),
                    }))
                  }
                />
              ))}

              {payload.embeds.length < 10 && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() =>
                    setPayload((p) => ({
                      ...p,
                      embeds: [...p.embeds, { ...EMPTY_EMBED }],
                    }))
                  }
                >
                  <Plus className="mr-1 h-4 w-4" /> Weiteres Embed ({payload.embeds.length} / 10)
                </Button>
              )}
            </div>
          )}
        </div>

        <div>
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Vorschau
          </h2>
          <div className="rounded-lg border bg-[#313338] p-4 text-white">
            {payload.content && (
              <div className="mb-2 whitespace-pre-wrap break-words text-sm">
                {renderDiscordText(payload.content, names)}
              </div>
            )}
            {payload.embeds.length === 0 && !payload.content && (
              <p className="text-sm italic text-white/40">Nichts zu zeigen.</p>
            )}
            <div className="space-y-2">
              {payload.embeds.map((e, i) => (
                <EmbedPreview key={i} embed={e} names={names} />
              ))}
            </div>
          </div>
        </div>
      </div>

      <Card className="mt-6">
        <CardContent className="pt-6">
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Gesendete Embeds
          </h2>
          <PostedList guildId={guildId!} type="embed" showEdit emptyText="Noch keine Embeds gesendet." />
        </CardContent>
      </Card>
    </div>
  );
}

// --------------------------- Editor ----------------------------

function EmbedEditor({
  index,
  guildId,
  embed,
  onChange,
  onRemove,
}: {
  index: number;
  guildId: string;
  embed: Embed;
  onChange: (patch: Partial<Embed>) => void;
  onRemove: () => void;
}) {
  const [open, setOpen] = useState(true);
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="mb-3 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="text-sm font-medium hover:text-primary"
          >
            {open ? '▾' : '▸'} Embed #{index + 1}
          </button>
          <Button variant="ghost" size="icon" onClick={onRemove}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
        {open && (
          <div className="space-y-3">
            <TwoColRow>
              <Field label="Autor-Name">
                <Input
                  value={embed.author?.name ?? ''}
                  onChange={(e) =>
                    onChange({
                      author: { ...(embed.author ?? {}), name: e.target.value },
                    })
                  }
                  maxLength={256}
                />
              </Field>
              <Field label="Autor-Icon URL">
                <Input
                  value={embed.author?.iconUrl ?? ''}
                  onChange={(e) =>
                    onChange({
                      author: { ...(embed.author ?? {}), iconUrl: e.target.value },
                    })
                  }
                  placeholder="https://…"
                />
              </Field>
            </TwoColRow>
            <Field label="Autor-URL">
              <Input
                value={embed.author?.url ?? ''}
                onChange={(e) =>
                  onChange({
                    author: { ...(embed.author ?? {}), url: e.target.value },
                  })
                }
                placeholder="https://…"
              />
            </Field>

            <Field label="Titel">
              <Input
                value={embed.title ?? ''}
                onChange={(e) => onChange({ title: e.target.value })}
                maxLength={256}
              />
            </Field>
            <Field label="Titel-URL (Link)">
              <Input
                value={embed.url ?? ''}
                onChange={(e) => onChange({ url: e.target.value })}
                placeholder="https://…"
              />
            </Field>
            <Field label="Beschreibung">
              <Textarea emoji
                value={embed.description ?? ''}
                onChange={(e) => onChange({ description: e.target.value })}
                className="min-h-[100px]"
                maxLength={4096}
              />
              <div className="mt-1 text-right text-xs text-muted-foreground">
                {(embed.description ?? '').length} / 4096
              </div>
            </Field>

            <TwoColRow>
              <Field label="Farbe">
                <input
                  type="color"
                  value={intToHex(embed.color ?? 0x5865f2)}
                  onChange={(e) => onChange({ color: hexToInt(e.target.value) })}
                  className="h-10 w-full rounded-md border border-input bg-background"
                />
              </Field>
              <Field label="Timestamp">
                <div className="flex h-10 items-center rounded-md border border-input bg-background px-3">
                  <Switch
                    checked={embed.timestamp === true}
                    onChange={(e) =>
                      onChange({ timestamp: e.target.checked ? true : undefined })
                    }
                    label="aktuelle Zeit anzeigen"
                  />
                </div>
              </Field>
            </TwoColRow>

            <TwoColRow>
              <Field label="Thumbnail URL">
                <div className="flex gap-2">
                  <Input
                    value={embed.thumbnailUrl ?? ''}
                    onChange={(e) => onChange({ thumbnailUrl: e.target.value })}
                    placeholder="https://…"
                  />
                  <MediaPicker
                    guildId={guildId}
                    currentUrl={embed.thumbnailUrl}
                    onSelect={(url) =>
                      onChange({ thumbnailUrl: window.location.origin + url })
                    }
                  />
                </div>
              </Field>
              <Field label="Großes Bild URL">
                <div className="flex gap-2">
                  <Input
                    value={embed.imageUrl ?? ''}
                    onChange={(e) => onChange({ imageUrl: e.target.value })}
                    placeholder="https://…"
                  />
                  <MediaPicker
                    guildId={guildId}
                    currentUrl={embed.imageUrl}
                    onSelect={(url) =>
                      onChange({ imageUrl: window.location.origin + url })
                    }
                  />
                </div>
              </Field>
            </TwoColRow>

            <FieldsEditor
              fields={embed.fields ?? []}
              onChange={(fields) => onChange({ fields })}
            />

            <TwoColRow>
              <Field label="Footer-Text">
                <Input
                  value={embed.footer?.text ?? ''}
                  onChange={(e) =>
                    onChange({
                      footer: { ...(embed.footer ?? {}), text: e.target.value },
                    })
                  }
                  maxLength={2048}
                />
              </Field>
              <Field label="Footer-Icon URL">
                <Input
                  value={embed.footer?.iconUrl ?? ''}
                  onChange={(e) =>
                    onChange({
                      footer: { ...(embed.footer ?? {}), iconUrl: e.target.value },
                    })
                  }
                  placeholder="https://…"
                />
              </Field>
            </TwoColRow>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function FieldsEditor({
  fields,
  onChange,
}: {
  fields: EmbedField[];
  onChange: (fields: EmbedField[]) => void;
}) {
  const update = (i: number, patch: Partial<EmbedField>) =>
    onChange(fields.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  return (
    <div>
      <Label>Fields ({fields.length} / 25)</Label>
      <div className="mt-1 space-y-2">
        {fields.map((f, i) => (
          <div key={i} className="rounded-md border p-2">
            <div className="flex gap-2">
              <Input
                value={f.name}
                onChange={(e) => update(i, { name: e.target.value })}
                placeholder="Feldname"
                maxLength={256}
                className="flex-1"
              />
              <Switch
                checked={!!f.inline}
                onChange={(e) => update(i, { inline: e.target.checked })}
                label="inline"
                className="text-xs"
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onChange(fields.filter((_, j) => j !== i))}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <Textarea emoji
              value={f.value}
              onChange={(e) => update(i, { value: e.target.value })}
              placeholder="Feld-Wert"
              maxLength={1024}
              className="mt-1 min-h-[60px]"
            />
          </div>
        ))}
        {fields.length < 25 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onChange([...fields, { name: '', value: '', inline: false }])}
          >
            <Plus className="mr-1 h-3 w-3" /> Feld hinzufügen
          </Button>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <Label>{label}</Label>
      {children}
    </div>
  );
}
function TwoColRow({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{children}</div>;
}

// --------------------------- Preview ---------------------------

function EmbedPreview({ embed, names }: { embed: Embed; names: MentionNames }) {
  const barColor = intToHex(embed.color ?? 0x5865f2);
  const timestampStr = useMemo(() => {
    if (embed.timestamp === true) return new Date().toLocaleString('de-DE');
    if (typeof embed.timestamp === 'string') {
      const d = new Date(embed.timestamp);
      return isNaN(d.getTime()) ? '' : d.toLocaleString('de-DE');
    }
    return '';
  }, [embed.timestamp]);

  const inlineGroups = groupInlineFields(embed.fields ?? []);

  return (
    <div
      className="flex overflow-hidden rounded-l-none rounded-r border-l-4 bg-[#2b2d31] text-sm"
      style={{ borderLeftColor: barColor }}
    >
      <div className="flex-1 p-3">
        {embed.author?.name && (
          <div className="mb-1 flex items-center gap-1.5 text-xs">
            {embed.author.iconUrl && (
              <img
                src={embed.author.iconUrl}
                alt=""
                className="h-4 w-4 rounded-full"
              />
            )}
            {embed.author.url ? (
              <a
                href={embed.author.url}
                target="_blank"
                rel="noreferrer"
                className="font-medium text-white hover:underline"
              >
                {embed.author.name}
              </a>
            ) : (
              <span className="font-medium text-white">{embed.author.name}</span>
            )}
          </div>
        )}
        {embed.title && (
          <div className="mb-1 font-semibold text-white">
            {embed.url ? (
              <a
                href={embed.url}
                target="_blank"
                rel="noreferrer"
                className="text-[#00a8fc] hover:underline"
              >
                {renderDiscordText(embed.title, names)}
              </a>
            ) : (
              renderDiscordText(embed.title, names)
            )}
          </div>
        )}
        {embed.description && (
          <div className="mb-2 whitespace-pre-wrap break-words text-white/90">
            {renderDiscordText(embed.description, names)}
          </div>
        )}

        {inlineGroups.length > 0 && (
          <div className="my-2 space-y-2">
            {inlineGroups.map((group, gi) => (
              <div
                key={gi}
                className={
                  group.length === 1
                    ? 'w-full'
                    : 'grid gap-2 grid-cols-' + Math.min(group.length, 3)
                }
                style={
                  group.length > 1
                    ? { gridTemplateColumns: `repeat(${Math.min(group.length, 3)}, minmax(0, 1fr))` }
                    : undefined
                }
              >
                {group.map((f, fi) => (
                  <div key={fi}>
                    <div className="text-xs font-semibold text-white">{f.name}</div>
                    <div className="whitespace-pre-wrap break-words text-xs text-white/85">
                      {renderDiscordText(f.value, names)}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {embed.imageUrl && (
          <img
            src={embed.imageUrl}
            alt=""
            className="mt-2 max-h-72 rounded-md"
          />
        )}

        {(embed.footer?.text || timestampStr) && (
          <div className="mt-2 flex items-center gap-1.5 text-xs text-white/60">
            {embed.footer?.iconUrl && (
              <img
                src={embed.footer.iconUrl}
                alt=""
                className="h-4 w-4 rounded-full"
              />
            )}
            {embed.footer?.text && <span>{embed.footer.text}</span>}
            {embed.footer?.text && timestampStr && <span>•</span>}
            {timestampStr && <span>{timestampStr}</span>}
          </div>
        )}
      </div>
      {embed.thumbnailUrl && (
        <img
          src={embed.thumbnailUrl}
          alt=""
          className="m-3 h-20 w-20 flex-shrink-0 rounded-md object-cover"
        />
      )}
    </div>
  );
}

// --------------------------- Helpers ---------------------------

function intToHex(n: number): string {
  return '#' + (n & 0xffffff).toString(16).padStart(6, '0');
}
function hexToInt(hex: string): number {
  return parseInt(hex.replace(/^#/, ''), 16) & 0xffffff;
}

/**
 * Strip empty strings from the payload so the API's zod URL validator doesn't
 * reject them, and drop entirely-empty embeds. URL fields also drop any value
 * that isn't a real http(s) URL — otherwise typing junk into a URL input
 * fails the entire send with a 400.
 */
const URL_KEYS = new Set(['url', 'iconUrl', 'thumbnailUrl', 'imageUrl']);
function cleanPayload(p: Payload): Payload {
  const stripStr = (v: unknown) =>
    typeof v === 'string' && v.trim().length === 0 ? undefined : v;
  const stripUrl = (v: unknown) => {
    if (typeof v !== 'string') return undefined;
    const t = v.trim();
    if (!t) return undefined;
    return /^https?:\/\/\S+$/i.test(t) ? t : undefined;
  };
  const cleanSubObject = (
    obj: Record<string, unknown> | undefined
  ): Record<string, unknown> | undefined => {
    if (!obj) return undefined;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj)) {
      const cleaned = URL_KEYS.has(k) ? stripUrl(v) : stripStr(v);
      if (cleaned !== undefined) out[k] = cleaned;
    }
    return out;
  };
  const embeds = p.embeds
    .map((e) => {
      const author = cleanSubObject(e.author);
      const footer = cleanSubObject(e.footer);
      const fields = (e.fields ?? []).filter((f) => f.name.trim() && f.value.trim());
      const cleaned: Embed = {
        title: stripStr(e.title) as string | undefined,
        description: stripStr(e.description) as string | undefined,
        url: stripUrl(e.url) as string | undefined,
        color: e.color,
        thumbnailUrl: stripUrl(e.thumbnailUrl) as string | undefined,
        imageUrl: stripUrl(e.imageUrl) as string | undefined,
        timestamp: e.timestamp,
      };
      if (author && Object.keys(author).length > 0) cleaned.author = author as Embed['author'];
      if (footer && Object.keys(footer).length > 0) cleaned.footer = footer as Embed['footer'];
      if (fields.length > 0) cleaned.fields = fields;
      // Remove undefined entries so it JSON-serialises cleanly.
      return Object.fromEntries(
        Object.entries(cleaned).filter(([, v]) => v !== undefined)
      ) as Embed;
    })
    .filter((e) => Object.keys(e).length > 0 && !(Object.keys(e).length === 1 && 'color' in e));
  return {
    content: p.content.trim(),
    embeds,
    ...(p.silent ? { silent: true } : {}),
  };
}

/**
 * Group consecutive inline fields (max 3 per row) like Discord does.
 * Non-inline fields always sit on their own row.
 */
function groupInlineFields(fields: EmbedField[]): EmbedField[][] {
  const rows: EmbedField[][] = [];
  let current: EmbedField[] = [];
  for (const f of fields) {
    if (f.inline) {
      current.push(f);
      if (current.length === 3) {
        rows.push(current);
        current = [];
      }
    } else {
      if (current.length > 0) {
        rows.push(current);
        current = [];
      }
      rows.push([f]);
    }
  }
  if (current.length > 0) rows.push(current);
  return rows;
}
